import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { Search, X } from 'lucide-react';
import GameSearchBox from './GameSearchBox';

const MAX_SCOPE_GAMES = 10;
const MAX_SCOPE_NAMES = 10;
const MAX_OPTION_RESULTS = 50;

export const EMPTY_SCOPE = { games: [], producers: [], providers: [], from: '', to: '' };

export function isScopeEmpty(scope) {
  return !scope || (scope.games.length === 0 && scope.producers.length === 0
    && scope.providers.length === 0 && !scope.from && !scope.to);
}

// One-line summary of a scope, shown under the question it was asked with.
export function describeScope(scope) {
  if (isScopeEmpty(scope)) return '';
  const parts = [
    ...scope.games.map(g => g.game_title),
    ...scope.providers.map(p => `provider: ${p}`),
    ...scope.producers.map(p => `producer: ${p}`),
  ];
  if (scope.from || scope.to) parts.push(`${scope.from || '…'} → ${scope.to || 'today'}`);
  return parts.join(' · ');
}

// Mirrors the backend's normalizeName: case, spaces and punctuation are
// ignored, so typing "Pragmatic Play" finds the stored "pragmaticplay".
function normalizeName(s) {
  return s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Type-to-filter picker over a fixed list of stored values (producers or
// providers), styled like GameSearchBox.
function OptionPicker({ options, selected, onSelect, placeholder, disabled }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Active names first, so a dead alias (e.g. "bgaming" after BGaming moved
  // to "bgmng") doesn't outrank the live one.
  const matches = useMemo(() => {
    const needle = normalizeName(query);
    return options
      .filter(o => !selected.includes(o.name) && (!needle || normalizeName(o.name).includes(needle)))
      .sort((a, b) => Number(b.active) - Number(a.active))
      .slice(0, MAX_OPTION_RESULTS);
  }, [options, selected, query]);

  function handleSelect(value) {
    setQuery('');
    setOpen(false);
    onSelect(value);
  }

  return (
    <div ref={boxRef} className="relative w-[200px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-mute pointer-events-none" />
      <Input
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="pl-8"
      />
      {open && !disabled && (
        <div className="absolute z-10 mt-1 w-full max-h-[240px] overflow-y-auto rounded-lg border border-white/10 bg-surface-2 shadow-xl">
          {matches.length === 0 ? (
            <p className="text-xs text-ink-dim px-3 py-3">No matches</p>
          ) : matches.map(o => (
            <button
              key={o.name}
              type="button"
              onClick={() => handleSelect(o.name)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-surface-3 transition-colors border-b border-white/5 last:border-0"
            >
              <span className={o.active ? 'text-ink' : 'text-ink-dim'}>{o.name}</span>
              {!o.active && <span className="text-[10px] text-ink-mute shrink-0">inactive</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// For each selected inactive name, the related names (same titles, likely
// the studio re-integrated) that are live and not yet selected.
function inactiveHints(selected, options) {
  const byName = new Map(options.map(o => [o.name, o]));
  return selected
    .map(name => byName.get(name))
    .filter(o => o && !o.active)
    .map(o => ({
      name: o.name,
      suggestions: (o.related || []).filter(r => byName.get(r)?.active && !selected.includes(r)),
    }));
}

function InactiveHint({ kind, hint, onAdd, disabled }) {
  return (
    <p className="text-[11px] text-ink-dim flex flex-wrap items-center gap-1.5">
      <span>
        <span className="text-ink">{hint.name}</span> ({kind}) has no live games
        {hint.suggestions.length > 0 ? ' — its titles now appear under:' : ', so recent periods may return no data.'}
      </span>
      {hint.suggestions.map(r => (
        <button
          key={r}
          type="button"
          onClick={() => onAdd(r)}
          disabled={disabled}
          className="rounded border border-cta/40 px-1.5 py-0.5 text-ink hover:bg-cta/15 disabled:opacity-40"
        >
          + {r}
        </button>
      ))}
    </p>
  );
}

function ScopeChip({ label, kind, onRemove, disabled }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-surface-2 pl-2 pr-1 py-0.5 text-[11px]">
      {kind && <span className="text-ink-dim">{kind}</span>}
      <span className="text-ink">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="p-0.5 rounded text-ink-dim hover:text-ink disabled:opacity-40"
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// Structured scope for an Ask AI question: exact games, providers,
// producers and a date range picked from real catalog values, sent with
// every question so the agent never has to guess stored spellings.
export default function AskScopeBar({ scope, onChange, options, disabled }) {
  const addUnique = (list, value, max) => (list.includes(value) || list.length >= max ? list : [...list, value]);

  const addGame = g => {
    if (scope.games.some(x => x.unique_game_id === g.unique_game_id) || scope.games.length >= MAX_SCOPE_GAMES) return;
    onChange({ ...scope, games: [...scope.games, g] });
  };
  const removeGame = id => onChange({ ...scope, games: scope.games.filter(g => g.unique_game_id !== id) });
  const addName = field => v => onChange({ ...scope, [field]: addUnique(scope[field], v, MAX_SCOPE_NAMES) });
  const removeName = (field, v) => onChange({ ...scope, [field]: scope[field].filter(x => x !== v) });

  const hasChips = scope.games.length + scope.producers.length + scope.providers.length > 0;
  const producerHints = inactiveHints(scope.producers, options.producers);
  const providerHints = inactiveHints(scope.providers, options.providers);

  return (
    <div className="rounded-lg border border-white/10 p-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-ink-dim">Scope (optional)</p>
        {!isScopeEmpty(scope) && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_SCOPE)}
            disabled={disabled}
            className="text-[11px] text-ink-dim hover:text-ink disabled:opacity-40"
          >
            Clear scope
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-[240px]">
          <label className="text-[11px] text-ink-dim mb-1 block">Game</label>
          <GameSearchBox onSelect={addGame} placeholder="Add a game…" />
        </div>
        <div>
          <label className="text-[11px] text-ink-dim mb-1 block">Provider (aggregator)</label>
          <OptionPicker
            options={options.providers}
            selected={scope.providers}
            onSelect={addName('providers')}
            placeholder="e.g. relax"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="text-[11px] text-ink-dim mb-1 block">Producer (studio)</label>
          <OptionPicker
            options={options.producers}
            selected={scope.producers}
            onSelect={addName('producers')}
            placeholder="e.g. boomerangstudios"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="text-[11px] text-ink-dim mb-1 block">From</label>
          <Input
            type="date" value={scope.from} max={scope.to || todayIso()} disabled={disabled}
            onChange={e => onChange({ ...scope, from: e.target.value })}
            className="w-[150px]"
          />
        </div>
        <div>
          <label className="text-[11px] text-ink-dim mb-1 block">To</label>
          <Input
            type="date" value={scope.to} min={scope.from || undefined} max={todayIso()} disabled={disabled}
            onChange={e => onChange({ ...scope, to: e.target.value })}
            className="w-[150px]"
          />
        </div>
      </div>

      {hasChips && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {scope.games.map(g => (
            <ScopeChip
              key={`g-${g.unique_game_id}`} kind="game" label={`${g.game_title} - ${g.provider}`}
              onRemove={() => removeGame(g.unique_game_id)} disabled={disabled}
            />
          ))}
          {scope.providers.map(p => (
            <ScopeChip key={`pv-${p}`} kind="provider" label={p} onRemove={() => removeName('providers', p)} disabled={disabled} />
          ))}
          {scope.producers.map(p => (
            <ScopeChip key={`pr-${p}`} kind="producer" label={p} onRemove={() => removeName('producers', p)} disabled={disabled} />
          ))}
        </div>
      )}

      {producerHints.length + providerHints.length > 0 && (
        <div className="flex flex-col gap-1 mt-2">
          {providerHints.map(h => (
            <InactiveHint key={`hv-${h.name}`} kind="provider" hint={h} onAdd={addName('providers')} disabled={disabled} />
          ))}
          {producerHints.map(h => (
            <InactiveHint key={`hp-${h.name}`} kind="producer" hint={h} onAdd={addName('producers')} disabled={disabled} />
          ))}
        </div>
      )}
    </div>
  );
}
